import React from 'react'
import classes from './typography.module.css'
import clsx from 'clsx'
import { useMediaQuery } from '@mantine/hooks'
import DOMPurify from 'dompurify'

type FontSizeKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl'

const FONT_STACKS: Record<string, string> = {
  Barlow:
    "'Barlow', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  'JetBrains Mono':
    "'JetBrains Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
}

interface BaseTypographyProps {
  className?: string
  level?: number
  fw?: number
  fc?: { light: string; dark: string }
  hc?: { light: string; dark: string }
  lh?: string
  ta?:
    | 'left'
    | 'right'
    | 'center'
    | 'justify'
    | 'start'
    | 'end'
    | 'match-parent'

  fs?: FontSizeKey
  ff?: string
  underline?: boolean
  tt?: string
  us?: string
}

interface TypographyProps extends BaseTypographyProps {
  children: React.ReactNode
}

interface HtmlTypographyProps extends BaseTypographyProps {
  children: string
}

const fontSizesOrder: FontSizeKey[] = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'xxl',
  'xxxl',
]

function getMobileSize(fs: FontSizeKey): FontSizeKey {
  const idx = fontSizesOrder.indexOf(fs)
  return idx > 0 ? fontSizesOrder[idx - 1] : fs
}

function getCommonStyles(
  fw: number,
  fc: { light: string; dark: string },
  hc: { light: string; dark: string } | undefined,
  lh: string,
  ta: string,
  appliedFs: FontSizeKey,
  ff: string,
  underline: boolean,
  tt: string,
  us: string
): React.CSSProperties {
  return {
    ...({
      '--fw': fw,
      '--ta': ta,
      '--lh': lh,
      '--fc-light': `var(--mantine-color-${fc.light})`,
      '--fc-dark': `var(--mantine-color-${fc.dark})`,
      '--fs': `var(--mantine-font-size-${appliedFs})`,
      '--ff': FONT_STACKS[ff] || ff,
      '--underline': underline ? 'underline' : 'none',
      '--tt': tt,
      '--us': us,
      ...(hc
        ? {
            '--hc-light': `var(--mantine-color-${hc.light})`,
            '--hc-dark': `var(--mantine-color-${hc.dark})`,
          }
        : {
            '--hc-light': `var(--mantine-color-${fc.light})`,
            '--hc-dark': `var(--mantine-color-${fc.dark})`,
          }),
    } as Record<string, string | number>),
  }
}

export function Typography({
  className,
  level,
  fw = 400,
  fc = { light: 'gray-9', dark: 'gray-3' },
  hc,
  lh = '150',
  ta = 'start',
  fs = 'md',
  ff = 'Barlow',
  underline = false,
  tt = 'none',
  us = 'auto',
  children,
}: TypographyProps) {
  const isMobile = useMediaQuery('(max-width: 50rem)')
  const appliedFs = isMobile ? getMobileSize(fs) : fs

  const commonStyles = getCommonStyles(
    fw,
    fc,
    hc,
    lh,
    ta,
    appliedFs,
    ff,
    underline,
    tt,
    us
  )

  if (level) {
    const safeLevel = Math.min(Math.max(level, 1), 6)
    const Tag = `h${safeLevel}` as keyof JSX.IntrinsicElements
    return (
      <Tag className={clsx(classes.typography, className)} style={commonStyles}>
        {children}
      </Tag>
    )
  }

  return (
    <div className={clsx(classes.typography, className)} style={commonStyles}>
      {children}
    </div>
  )
}

export function HtmlTypography({
  className,
  level,
  fw = 400,
  fc = { light: 'gray-9', dark: 'gray-3' },
  hc,
  lh = '150',
  ta = 'start',
  fs = 'md',
  ff = 'Barlow',
  underline = false,
  tt = 'none',
  us = 'auto',
  children,
}: HtmlTypographyProps) {
  const isMobile = useMediaQuery('(max-width: 50rem)')
  const appliedFs = isMobile ? getMobileSize(fs) : fs

  const commonStyles = getCommonStyles(
    fw,
    fc,
    hc,
    lh,
    ta,
    appliedFs,
    ff,
    underline,
    tt,
    us
  )

  const sanitizedHtml = { __html: DOMPurify.sanitize(children) }

  if (level) {
    const safeLevel = Math.min(Math.max(level, 1), 6)
    const Tag = `h${safeLevel}` as keyof JSX.IntrinsicElements
    return (
      <Tag
        className={clsx(classes.typography, className)}
        style={commonStyles}
        dangerouslySetInnerHTML={sanitizedHtml}
      />
    )
  }

  return (
    <div
      className={clsx(classes.typography, className)}
      style={commonStyles}
      dangerouslySetInnerHTML={sanitizedHtml}
    />
  )
}
